import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getChat, postChat } from '../../api/twoDays/board.api';
import { cn } from './model/derive';
import { KST, formatClock } from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';
import useMedia, { MOBILE } from './useMedia';
import usePresence from './usePresence';
import Linkify from './Linkify';

/**
 * 팀원 ⇄ 운영진 메신저 (팀원 쪽) — 워크보드 모든 화면 오른쪽 아래 떠 있는 버튼(56px) + 채팅 패널.
 *   데스크톱: 패널 380 × 최대 600, 오른쪽 아래 (버튼은 그대로 두고 X 로 바뀐다)
 *   모바일: 위에서 8% 내려온 바닥 시트 + 어두운 막, 버튼은 숨긴다. 태스크 상세에서는 아래 행동 막대 위로
 * 열려 있는 동안 3초마다 다시 읽고 읽음으로 남긴다. 닫혀 있으면 20초마다 안 읽은 수만.
 *   비어 있으면 안내 카드 + 주제 칩 (고르면 바로 보낸다 · "Something else" 는 입력칸으로)
 *   첫 메시지 → "Connecting you to ops…" → "Ops got your message…" → 운영진이 답하면 "Ops team joined the chat"
 *   운영진이 쓰는 중이면 점 세 개 + "Ops is typing"
 */

const OPEN_POLL_MS = 3_000;
const CLOSED_POLL_MS = 20_000;
const TOPICS = ["A teammate isn't responding", "I can't open a file or link", 'I need more time on a task', 'Something else'];
const STATUS = {
  none: ['Online · replies in a few minutes', 'bg-green-deep02'],
  waiting: ['Connecting you to ops…', 'bg-functional-warning03'],
  open: ['Ops team · in this chat', 'bg-green-deep02'],
  resolved: ['Resolved · send a message to reopen', 'bg-grayscale-gray05'],
};
const clock = (iso) => (iso ? formatClock(new Date(iso), KST) : '');

const SendIcon = () => (
  <svg viewBox='0 0 20 20' fill='none' aria-hidden className='h-5 w-5'>
    <path d='M10 16V4M10 4l-5 5M10 4l5 5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);
const ChatIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' aria-hidden className='h-[26px] w-[26px]'>
    <path d='M12 4.5c-4.4 0-8 2.9-8 6.5 0 1.8.9 3.5 2.4 4.7L5.5 19.5l3.9-1.5c.8.2 1.7.3 2.6.3 4.4 0 8-2.9 8-6.5s-3.6-7.3-8-7.3Z' stroke='currentColor' strokeWidth='1.6' strokeLinejoin='round' />
  </svg>
);
const CloseIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='none' aria-hidden className={className}>
    <path d='M5 5l10 10M15 5L5 15' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
  </svg>
);

/** 말풍선 — 시스템 메시지는 가운데 줄, 내 것은 오른쪽 초록, 운영진은 왼쪽 흰색. 이어지는 상대 메시지는 이름을 한 번만 */
export const Bubbles = ({ messages, mine, who, max = 'max-w-[84%]' }) => {
  let prev = null;
  return messages.map((m) => {
    if (m.sender === 'system') {
      prev = null;
      return (
        <li key={m.id} className='my-1 flex items-center gap-2 text-[12px] leading-[140%] text-text-tertiary'>
          <span className='h-px flex-1 bg-green-deep03/[0.1]' />
          <span className='text-center [text-wrap:balance]'>
            {m.body} · {clock(m.createdAt)}
          </span>
          <span className='h-px flex-1 bg-green-deep03/[0.1]' />
        </li>
      );
    }
    const own = m.sender === mine;
    const showWho = !own && prev !== m.sender;
    prev = m.sender;
    const ref = m.taskTitle ? `Task · ${m.taskTitle}` : m.refLabel;
    return (
      <li key={m.id} className={cn('flex flex-col gap-1', own ? 'items-end' : 'items-start')}>
        {showWho && <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>{who(m.sender)}</span>}
        <div
          className={cn(
            'flex flex-col gap-1.5 whitespace-pre-wrap px-3.5 py-2.5 text-[14px] leading-[150%] [overflow-wrap:anywhere]',
            max,
            own
              ? 'rounded-[16px_16px_4px_16px] bg-green-light02 text-text-primary'
              : 'rounded-[16px_16px_16px_4px] bg-grayscale-gray00 text-text-primary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]',
          )}
        >
          <Linkify text={m.body} />
          {ref && (
            <span className={cn('inline-flex min-h-[22px] max-w-full items-center self-start rounded-[11px] px-[9px] py-0.5 text-[12px] font-medium leading-[140%] [overflow-wrap:anywhere]', own ? 'bg-grayscale-gray00/60 text-green-deep05' : 'bg-surface-green10 text-green-deep04')}>
              {ref}
            </span>
          )}
        </div>
        <span className='text-[11px] leading-[140%] tabular-nums text-text-tertiary'>{clock(m.createdAt)}</span>
      </li>
    );
  });
};

export const TypingDots = () => (
  <span className='inline-flex gap-1 rounded-[16px_16px_16px_4px] bg-grayscale-gray00 px-3.5 py-2.5 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]'>
    {[0, 1, 2].map((i) => (
      <span key={i} className='td-oc-dot h-1.5 w-1.5 rounded-full bg-grayscale-gray05' style={{ animationDelay: `${i * 150}ms` }} />
    ))}
  </span>
);

/** 보내기 — 44px 원형, 글이 없으면 흐리게 */
export const SendButton = ({ disabled, onClick }) => (
  <button
    type='button'
    aria-label='Send'
    disabled={disabled}
    onClick={onClick}
    className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', disabled ? 'bg-green-deep03/[0.06] text-text-disable' : 'bg-green-deep02 text-grayscale-gray00 hover:bg-green-deep04')}
  >
    <SendIcon />
  </button>
);

export const composerClass =
  'max-h-[120px] min-h-[44px] flex-1 resize-none rounded-[22px] border border-green-deep03/20 bg-grayscale-gray00 px-3.5 py-[11px] text-[14px] leading-[150%] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none';

const OpsChat = ({ open, onOpen, onClose, taskId, lifted = false }) => {
  const { data, live, slug, now } = useWorkboardCtx();
  const mobile = useMedia(MOBILE);
  const [mine, setMine] = useState(null);
  const [local, setLocal] = useState([]); // 목 보드 — 화면에서만
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const boxRef = useRef(null);
  const panelRef = useRef(null);
  const fabRef = useRef(null);
  // 열고 닫을 때 전환 — 데스크톱은 버튼 쪽(오른쪽 아래)에서 자라나고, 모바일은 아래에서 올라온다
  const { mounted, shown } = usePresence(open, 260);
  const thread = live ? mine?.thread : { status: local.length ? 'waiting' : 'none', messages: local, typing: false, unread: 0 };
  const messages = thread?.messages ?? [];
  const status = messages.length ? (thread?.status ?? 'none') : 'none';
  const unread = open ? 0 : (thread?.unread ?? 0);

  const load = useCallback(() => (live && slug ? getChat(slug).then(setMine).catch(() => {}) : Promise.resolve()), [live, slug]);
  useEffect(() => {
    if (!live) return undefined;
    load();
    const id = window.setInterval(load, open ? OPEN_POLL_MS : CLOSED_POLL_MS);
    return () => window.clearInterval(id);
  }, [live, open, load]);
  // 열려 있으면 읽음 — 안 읽은 운영진 메시지가 있을 때만
  useEffect(() => {
    if (open && live && mine?.thread?.unread > 0) postChat(slug, { type: 'read' }).then(setMine).catch(() => {});
  }, [open, live, slug, mine?.thread?.unread]);
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, thread?.typing, open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    // 패널과 버튼 밖을 누르면 닫는다
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || fabRef.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, onClose]);

  const send = async (bodyIn) => {
    const body = (bodyIn ?? text).trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      if (live) setMine(await postChat(slug, { type: 'send', body, taskId: taskId || null }));
      else {
        setLocal((l) => [
          ...l,
          { id: `m-${Date.now()}`, sender: 'member', body, createdAt: now.toISOString() },
          ...(l.length ? [] : [{ id: `s-${Date.now()}`, sender: 'system', body: 'Connecting you to ops…', createdAt: now.toISOString() }]),
        ]);
      }
      setText('');
    } catch (e) {
      setError(e?.response?.data?.msg || 'Couldn’t send. Try again.');
    } finally {
      setSending(false);
    }
  };
  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      send();
    }
  };
  const [statusText, dot] = STATUS[status] ?? STATUS.none;
  const me = data.members.find((m) => m.id === data.viewerId)?.name ?? '';

  const fab = (!open || !mobile) && (
    <button
      ref={fabRef}
      data-keep-panel
      type='button'
      onClick={open ? onClose : onOpen}
      title='Chat with ops'
      aria-label={open ? 'Close chat with ops' : `Chat with ops${unread ? ` · ${unread} new` : ''}`}
      className={cn(
        'fixed z-[46] flex h-14 w-14 items-center justify-center rounded-full bg-green-deep02 text-grayscale-gray00 shadow-popup-01 transition-transform duration-200 active:scale-95 motion-reduce:transition-none',
        mobile ? (lifted ? 'bottom-[88px] right-4' : 'bottom-4 right-4') : 'bottom-6 right-6',
      )}
    >
      {/* 아이콘이 돌며 바뀐다 — 말풍선 ↔ X */}
      <span className={cn('absolute flex items-center justify-center transition-[transform,opacity] duration-300 motion-reduce:transition-none', open ? 'rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100')}>
        <ChatIcon />
      </span>
      <span className={cn('absolute flex items-center justify-center transition-[transform,opacity] duration-300 motion-reduce:transition-none', open ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-50 opacity-0')}>
        <CloseIcon className='h-[22px] w-[22px]' />
      </span>
      {!open && unread > 0 && (
        <span className='absolute -right-0.5 -top-0.5 box-border flex h-5 min-w-5 items-center justify-center rounded-full bg-functional-failed03 px-1.5 text-[11px] font-medium leading-5 text-grayscale-gray00 shadow-[0_0_0_2px_#fff]'>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );

  const panel = mounted && (
    <>
      {mobile && <div aria-hidden onClick={onClose} className={cn('fixed inset-0 z-[44] bg-surface-black40 transition-opacity duration-300 motion-reduce:transition-none', shown ? 'opacity-100' : 'opacity-0')} />}
      <section
        ref={panelRef}
        role='dialog'
        aria-label='Chat with ops'
        className={cn(
          'fixed z-[45] flex flex-col overflow-hidden border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-popup-01',
          'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
          mobile
            ? cn('inset-x-0 bottom-0 top-[8%] rounded-t-3xl', shown ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0')
            : cn('bottom-[92px] right-6 h-[min(600px,calc(100vh-120px))] w-[380px] origin-bottom-right rounded-[20px]', shown ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-[0.92] opacity-0'),
        )}
      >
        <header className='flex items-center gap-3 border-b border-green-deep03/[0.08] py-3.5 pl-4 pr-3'>
          <span className='relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-deep04 text-[12px] font-medium text-grayscale-gray00'>
            Ops
            <span className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full shadow-[0_0_0_2px_#fff]', dot)} />
          </span>
          <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
            <span className='text-[15px] font-medium leading-[140%] text-text-primary'>Ops team</span>
            <span className='text-[12px] leading-[140%] text-text-secondary'>{statusText}</span>
          </div>
          <button type='button' onClick={onClose} aria-label='Close chat' className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-icon-secondary hover:bg-green-deep03/[0.06]'>
            <CloseIcon className='h-5 w-5' />
          </button>
        </header>
        <div className='flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-4'>
          {messages.length === 0 && (
            <div className='mt-auto flex flex-col gap-3.5'>
              <div className='flex flex-col gap-1 rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3.5'>
                <span className='text-[14px] font-medium leading-[140%] text-text-primary'>Need help from ops{me ? `, ${me.split(' ')[0]}` : ''}?</span>
                <span className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>Ask about tasks, teammates or the project. Ops sees your team and tasks, and replies here.</span>
              </div>
              <div className='flex flex-col gap-1.5'>
                <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>Pick a topic or type below</span>
                <div className='flex flex-wrap gap-1.5'>
                  {TOPICS.map((t) => (
                    <button key={t} type='button' onClick={() => (t === 'Something else' ? boxRef.current?.focus() : send(t))} className='min-h-9 rounded-full bg-green-deep03/[0.08] px-3 py-1.5 text-left text-[13px] leading-[140%] text-text-primary hover:bg-green-deep03/[0.14]'>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.length > 0 && (
            <ul className='flex flex-col gap-2.5'>
              <Bubbles messages={messages} mine='member' who={() => 'Ops team'} />
            </ul>
          )}
          {thread?.typing && (
            <div className='flex items-center gap-2 text-[12px] text-text-secondary'>
              <TypingDots />
              Ops is typing
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className='flex flex-col gap-1.5 border-t border-green-deep03/[0.08] p-3'>
          <div className='flex items-end gap-2'>
            <textarea ref={boxRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={1} maxLength={2000} placeholder='Message ops' aria-label='Message ops' className={composerClass} />
            <SendButton disabled={!text.trim() || sending} onClick={() => send()} />
          </div>
          {error && <p className='text-[12px] text-functional-failed04'>{error}</p>}
        </div>
      </section>
    </>
  );

  return ReactDOM.createPortal(
    <>
      {panel}
      {fab}
    </>,
    document.body,
  );
};

export default OpsChat;
